if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileMq = window.matchMedia('(max-width: 749px)');

        // State for the mobile 100vh swipe engine.
        this.snapState = {
          isLocked: false,
          isTransit: false,
          currentIndex: 0,
          touchStartY: 0,
          touchStartX: 0,
          lastTouchY: 0,
          dragDy: 0,
          axis: null,
          wheelAccum: 0,
          lastScrollY: window.scrollY,
          prevTop: null,
          lockY: 0,
          programmatic: false,
          releaseAt: 0,
          ANIM_MS: 480,
          SWIPE_THRESH: 42,
          WHEEL_THRESH: 60,
        };

        this._vh = window.innerHeight * 0.65; // mobile gallery height (65vh), pixel-locked vs dvh shifts

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.snapState.ANIM_MS = 0;
        }

        // Mobile-only behaviours. Both are safe no-ops on desktop.
        this.clBuildDots();
        this.clInitSnap();

        // Re-evaluate when crossing the 750px breakpoint (resize / rotate).
        this.mobileMq.addEventListener('change', () => this.clHandleBreakpoint());

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      /* =========================================================
         CL: mobile vertical progress dots
         ========================================================= */
      clBuildDots() {
        this.navDotsContainer = this.querySelector('[data-cl-gallery-dots]');
        const viewer = this.elements.viewer;
        if (!this.navDotsContainer || !viewer) return;

        this.dotsArray = [];
        this.navDotsContainer.innerHTML = '';
        this.navDotsContainer.classList.add('cl-dots--off');

        const items = Array.from(viewer.querySelectorAll('.product__media-item'));
        if (items.length < 2) return;

        items.forEach((item, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'cl-gallery-dot';
          b.setAttribute('role', 'tab');
          b.setAttribute('aria-label', 'Go to image ' + (i + 1));
          b.addEventListener('click', () => {
            if (this.snapState.isLocked) {
              this.clSnapGoTo(i);
            } else {
              this.clEngage(false);
              this.clSnapGoTo(i);
            }
          });
          this.navDotsContainer.appendChild(b);
          this.dotsArray.push(b);
        });

        this.clSnapSyncDots(this.snapState.currentIndex);
      }

      clShowDots(show) {
        if (!this.navDotsContainer) return;
        this.navDotsContainer.classList.toggle('cl-dots--off', !show);
      }

      // Dots are visible whenever the gallery is in view on mobile, not only
      // while engaged. This is what makes them show immediately on load.
      clUpdateDotsVisibility() {
        if (!this.navDotsContainer || !this.dotsArray || !this.dotsArray.length) return;
        if (!this.mobileMq.matches) {
          this.clShowDots(false);
          return;
        }
        if (this.snapState.isLocked) {
          this.clShowDots(true);
          return;
        }
        const r = this.viewerEl ? this.viewerEl.getBoundingClientRect() : this.getBoundingClientRect();
        const mid = window.innerHeight * 0.5;
        this.clShowDots(r.top < mid && r.bottom > mid);
      }

      clSnapSyncDots(idx) {
        if (!this.dotsArray || !this.dotsArray.length) return;
        this.dotsArray.forEach((d, i) => {
          const on = i === idx;
          d.classList.toggle('is-active', on);
          d.setAttribute('aria-selected', String(on));
        });
      }

      /* =========================================================
         CL: mobile pinned 100vh swipe engine
         ========================================================= */
      clItems() {
        return this.snapList ? Array.from(this.snapList.querySelectorAll('.product__media-item')) : [];
      }

      clSetVH() {
        this._vh = window.innerHeight * 0.65;
        this.style.setProperty('--cl-vh', this._vh + 'px');
      }

      clInitSnap() {
        this.viewerEl = this.elements.viewer;
        this.snapList = this.querySelector('.product__media-list');
        if (!this.viewerEl || !this.snapList) return;

        this._onScroll = this.clOnScroll.bind(this);
        this._onWheel = this.clOnWheel.bind(this);
        this._onTouchStart = this.clOnTouchStart.bind(this);
        this._onTouchMove = this.clOnTouchMove.bind(this);
        this._onTouchEnd = this.clOnTouchEnd.bind(this);
        this._onResize = () => {
          this.clSetVH();
          this.clReframe();
          this.clUpdateDotsVisibility();
        };

        window.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('wheel', this._onWheel, { passive: false });
        this.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this.addEventListener('touchmove', this._onTouchMove, { passive: false });
        this.addEventListener('touchend', this._onTouchEnd, { passive: false });
        window.addEventListener('resize', this._onResize, { passive: true });
        window.addEventListener('orientationchange', this._onResize);

        // Keep position (don't jump to slide 0) when the media list re-renders.
        this.snapMO = new MutationObserver(() => {
          this.clBuildDots();
          const items = this.clItems();
          if (this.snapState.currentIndex > items.length - 1) {
            this.snapState.currentIndex = Math.max(0, items.length - 1);
          }
          this.clSnapApplyTranslate(this.snapState.currentIndex, false);
          this.clSnapSyncDots(this.snapState.currentIndex);
          this.clUpdateDotsVisibility();
        });
        this.snapMO.observe(this.snapList, { childList: true });

        this.clSetVH();
        this.clHandleBreakpoint();

        // Show dots straight away if the gallery is already on screen.
        this.clUpdateDotsVisibility();
        requestAnimationFrame(() => this.clUpdateDotsVisibility());
        window.addEventListener('load', () => {
          if (!this.dotsArray || !this.dotsArray.length) this.clBuildDots();
          this.clSetVH();
          this.clHandleBreakpoint();
          this.clUpdateDotsVisibility();
        });

        // Engage if the gallery is pinned to the top on load.
        setTimeout(() => {
          if (!this.mobileMq.matches || this.snapState.isLocked) return;
          if (this.clItems().length < 2) return;
          const rect = this.viewerEl.getBoundingClientRect();
          if (rect.top <= 1 && rect.bottom > window.innerHeight * 0.5) this.clEngage(false);
        }, 100);
      }

      clHandleBreakpoint() {
        if (!this.snapList) return;
        if (this.mobileMq.matches && this.clItems().length >= 2) {
          this.classList.add('cl-snap-ready');
          this.clSetVH();
          if (!this.snapState.isLocked) this.clSnapApplyTranslate(this.snapState.currentIndex, false);
        } else {
          this.clTeardownSnap();
        }
      }

      clTeardownSnap() {
        if (this.snapState.isLocked) {
          this.snapState.isLocked = false;
          this.snapState.programmatic = false;
        }
        this.classList.remove('cl-snap-ready', 'cl-snap-engaged');
        if (this.snapList) {
          this.snapList.style.transition = '';
          this.snapList.style.transform = '';
        }
        this.clShowDots(false);
      }

      clSnapApplyTranslate(idx, animated) {
        if (!this.snapList) return;
        const vh = this._vh || window.innerHeight;
        this.snapList.style.transition = animated
          ? `transform ${this.snapState.ANIM_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`
          : 'none';
        // Pixel-based (not dvh) so the address bar collapsing never shifts it.
        this.snapList.style.transform = `translate3d(0, ${Math.round(idx * -vh)}px, 0)`;
      }

      clEngage(fromBelow) {
        if (this.snapState.isLocked) return;
        const items = this.clItems();
        if (items.length < 2) return;

        this.clSetVH();
        const rect = this.viewerEl.getBoundingClientRect();
        const docTop = Math.round(window.scrollY + rect.top);

        this.snapState.lockY = docTop;
        this.snapState.isLocked = true;
        this.snapState.currentIndex = fromBelow ? items.length - 1 : 0;

        if (this.snapState.lastTouchY) this.snapState.touchStartY = this.snapState.lastTouchY;
        this.snapState.dragDy = 0;
        this.snapState.axis = 'y';

        if (Math.abs(window.scrollY - docTop) > 1) {
          this.snapState.programmatic = true;
          window.scrollTo(0, docTop);
          requestAnimationFrame(() => {
            this.snapState.programmatic = false;
          });
        }

        this.classList.add('cl-snap-engaged');
        this.clSnapApplyTranslate(this.snapState.currentIndex, false);
        this.clSnapSyncDots(this.snapState.currentIndex);
        this.clShowDots(true);
        this.preventStickyHeader();
      }

      clRelease(dir) {
        if (!this.snapState.isLocked) return;
        this.snapState.isLocked = false;
        this.snapState.releaseAt = Date.now();
        this.classList.remove('cl-snap-engaged');

        // Settle instantly on the edge image so no transition competes with
        // the page scroll we are about to trigger.
        this.clSnapApplyTranslate(this.snapState.currentIndex, false);
        this.snapState.lastScrollY = window.scrollY;
        this.snapState.prevTop = this.viewerEl.getBoundingClientRect().top;

        // Carry the releasing swipe into a smooth page scroll instead of a
        // dead stop that needs a second swipe.
        const vh = this._vh || window.innerHeight;
        const target =
          dir === 'down'
            ? this.snapState.lockY + this.viewerEl.offsetHeight
            : Math.max(0, this.snapState.lockY - vh);

        requestAnimationFrame(() => {
          window.scrollTo({ top: target, behavior: 'smooth' });
          this.clUpdateDotsVisibility();
        });
      }

      clSnapGoTo(newIdx) {
        if (this.snapState.isTransit) return;
        const items = this.clItems();

        if (newIdx < 0) {
          this.clRelease('up');
          return;
        }
        if (newIdx >= items.length) {
          this.clRelease('down');
          return;
        }

        this.snapState.isTransit = true;
        this.snapState.currentIndex = newIdx;
        this.clSnapApplyTranslate(newIdx, true);
        this.clSnapSyncDots(newIdx);

        const item = items[newIdx];
        if (item) this.playActiveMedia(item);

        clearTimeout(this._transitT);
        this._transitT = setTimeout(() => {
          this.snapState.isTransit = false;
        }, this.snapState.ANIM_MS || 16);
      }

      clOnScroll() {
        if (!this.mobileMq.matches) return;
        if (this.snapState.programmatic) return;

        if (this.snapState.isLocked) {
          if (Math.abs(window.scrollY - this.snapState.lockY) > 2) {
            this.snapState.programmatic = true;
            window.scrollTo(0, this.snapState.lockY);
            requestAnimationFrame(() => {
              this.snapState.programmatic = false;
            });
          }
          this.snapState.lastScrollY = window.scrollY;
          return;
        }

        const items = this.clItems();
        if (items.length < 2) {
          this.snapState.lastScrollY = window.scrollY;
          return;
        }

        this.clUpdateDotsVisibility();

        if (Date.now() - this.snapState.releaseAt < 350) {
          this.snapState.lastScrollY = window.scrollY;
          this.snapState.prevTop = this.viewerEl.getBoundingClientRect().top;
          return;
        }

        const rect = this.viewerEl.getBoundingClientRect();
        const top = rect.top;
        const bottom = rect.bottom;
        const prev = this.snapState.prevTop == null ? top : this.snapState.prevTop;
        const sy = window.scrollY;
        const goingDown = sy >= this.snapState.lastScrollY;
        const vh = window.innerHeight;

        this.snapState.lastScrollY = sy;
        this.snapState.prevTop = top;

        if (goingDown && prev > 0 && top <= 0 && bottom > vh * 0.35) {
          this.clEngage(false);
          return;
        }
        if (!goingDown && prev < 0 && top >= 0 && top < vh * 0.65) {
          this.clEngage(true);
        }
      }

      clOnWheel(e) {
        if (!this.snapState.isLocked || this.snapState.isTransit) return;
        e.preventDefault();
        this.snapState.wheelAccum += e.deltaY;
        if (Math.abs(this.snapState.wheelAccum) >= this.snapState.WHEEL_THRESH) {
          const dir = this.snapState.wheelAccum > 0 ? 1 : -1;
          this.snapState.wheelAccum = 0;
          this.clSnapGoTo(this.snapState.currentIndex + dir);
        }
      }

      clOnTouchStart(e) {
        const t = e.touches[0];
        this.snapState.touchStartY = t.clientY;
        this.snapState.touchStartX = t.clientX;
        this.snapState.lastTouchY = t.clientY;
        this.snapState.dragDy = 0;
        this.snapState.axis = null;
      }

      clOnTouchMove(e) {
        this.snapState.lastTouchY = e.touches[0].clientY;
        if (!this.snapState.isLocked) return;

        e.preventDefault();
        if (this.snapState.isTransit) return;

        const t = e.touches[0];
        const dy = t.clientY - this.snapState.touchStartY;
        const dx = t.clientX - this.snapState.touchStartX;

        if (this.snapState.axis == null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
          this.snapState.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
        if (this.snapState.axis === 'x') return;

        this.snapState.dragDy = dy;

        const items = this.clItems();
        const last = items.length - 1;
        const vh = this._vh || window.innerHeight;
        let eff = dy;
        // Heavily damped, capped overscroll at the ends so we never reveal
        // empty space above the first / below the last image.
        if ((this.snapState.currentIndex === 0 && dy > 0) || (this.snapState.currentIndex === last && dy < 0)) {
          eff = Math.sign(dy) * Math.min(Math.abs(dy) * 0.22, 70);
        }

        const base = -this.snapState.currentIndex * vh;
        this.snapList.style.transition = 'none';
        this.snapList.style.transform = `translate3d(0, ${Math.round(base + eff)}px, 0)`;
      }

      clOnTouchEnd() {
        if (!this.snapState.isLocked) return;

        const dy = this.snapState.dragDy;
        const axis = this.snapState.axis;
        const idx = this.snapState.currentIndex;
        const last = this.clItems().length - 1;
        const TH = this.snapState.SWIPE_THRESH;

        if (axis === 'x' || Math.abs(dy) < TH) {
          this.clSnapGoTo(idx);
        } else if (dy < 0) {
          idx === last ? this.clRelease('down') : this.clSnapGoTo(idx + 1);
        } else {
          idx === 0 ? this.clRelease('up') : this.clSnapGoTo(idx - 1);
        }

        this.snapState.dragDy = 0;
        this.snapState.axis = null;
      }

      clReframe() {
        if (!this.mobileMq.matches) {
          this.clTeardownSnap();
          return;
        }
        if (this.snapState.isLocked) {
          const rect = this.viewerEl.getBoundingClientRect();
          this.snapState.lockY = Math.round(window.scrollY + rect.top);
        }
        this.clSnapApplyTranslate(this.snapState.currentIndex, false);
      }

      /* ===================== unchanged theme behaviour ===================== */
      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.snapState.isLocked) return; // don't fight the mobile snap engine
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}